import React, { useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import SelectSocialPlatfrom from "../SocialMedia/SelectSocialPlatfrom";
import { useEventStore } from "../../store/eventStore";
import defaultTemplateImage from "../../assets/images/template-default.png"; // ✅ ADD
import "../event/EventSocialPost.style.css";

const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;
const CANVAS_STATE_COMMENT_PREFIX = "CANVAS_STATE_V1:";

const decodeCanvasState = (encoded) => {
  if (!encoded) return null;
  try {
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

const extractTemplateBaseSize = (html) => {
  if (!html || !html.trim()) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const container = doc.body.firstElementChild || doc.body;

    const encodedCanvasState =
      container.getAttribute?.("data-canvas-state") ||
      container.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state") ||
      doc.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state");

    const commentMatch = html.match(
      new RegExp(`<!--\\s*${CANVAS_STATE_COMMENT_PREFIX}([A-Za-z0-9+/=]+)\\s*-->`),
    );
    const encodedFromComment = commentMatch?.[1] || "";
    const decodedCanvasState =
      decodeCanvasState(encodedCanvasState) || decodeCanvasState(encodedFromComment);

    const baseWidth = Number(decodedCanvasState?.baseWidth) || 0;
    const baseHeight = Number(decodedCanvasState?.baseHeight) || 0;
    if (baseWidth > 0 && baseHeight > 0) {
      return { width: baseWidth, height: baseHeight };
    }

    const parseAbsoluteDimension = (rawValue) => {
      if (typeof rawValue !== "string") return 0;
      const trimmed = rawValue.trim();
      if (!trimmed || trimmed.endsWith("%")) return 0;
      const numeric = parseFloat(trimmed);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
    };

    const rootWidth = parseAbsoluteDimension(container.style?.width || "");
    const rootHeight = parseAbsoluteDimension(container.style?.height || "");
    if (
      Number.isFinite(rootWidth) &&
      Number.isFinite(rootHeight) &&
      rootWidth > 0 &&
      rootHeight > 0
    ) {
      return { width: rootWidth, height: rootHeight };
    }
  } catch (error) {
    return null;
  }

  return null;
};

const hasSerializedCanvasState = (html = "") =>
  html.includes("data-canvas-state=") || html.includes(CANVAS_STATE_COMMENT_PREFIX);

// Content Styles
const ModalContent = styled.div`
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const PluginContainer = styled.div`
  margin: 0 auto;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  opacity: 0.9;
`;

const Content = styled.div`
  padding: 32px;
`;

const CenteredImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 24px 0;
`;

const CenteredImage = styled.img`
  width: 50%;
  max-width: 500px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const EventSocialPost = () => {
  const campaignData = useEventStore((state) => state.eventDetails);

  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const templateBodyRef = useRef(null);
  const templateBaseSize = useMemo(
    () => extractTemplateBaseSize(campaignData.templateHtml),
    [campaignData.templateHtml],
  );
  const renderWidth = templateBaseSize?.width || imageSize.width;
  const renderHeight = templateBaseSize?.height || imageSize.height;

  // ✅ DEFINE IMG HERE
  const img = campaignData.templateImage
    ? campaignData.templateImage.startsWith("http")
      ? campaignData.templateImage
      : `${BASE_URL}${campaignData.templateImage}`
    : defaultTemplateImage;

  // ✅ Load image size
  useEffect(() => {
    const image = new Image();
    image.src = img;

    image.onload = () => {
      setImageSize({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
  }, [img]);

  // Ensure text doesn't overflow in shapes and auto-adjust font size, and center text
  useEffect(() => {
    if (!templateBodyRef.current) return;
    
    const container = templateBodyRef.current;
    const shapes = container.querySelectorAll('div[style*="position: absolute"]');
    
    shapes.forEach((shape) => {
      // Ensure shape container clips content and centers content
      if (!shape.style.overflow || shape.style.overflow === 'visible') {
        shape.style.overflow = 'hidden';
      }
      
      // Ensure shape container uses flexbox for centering
      shape.style.display = 'flex';
      shape.style.alignItems = 'center';
      shape.style.justifyContent = 'center';

      // Backward-compat fix: older saved circles used equal width/height % values,
      // which render as ovals when container width/height differ.
      const widthPercent = shape.style.width?.endsWith('%') ? parseFloat(shape.style.width) : NaN;
      const heightPercent = shape.style.height?.endsWith('%') ? parseFloat(shape.style.height) : NaN;
      const isLegacyCircleCandidate =
        shape.style.borderRadius === '50%' &&
        !shape.style.clipPath &&
        !shape.style.webkitClipPath &&
        Number.isFinite(widthPercent) &&
        Number.isFinite(heightPercent) &&
        Math.abs(widthPercent - heightPercent) < 0.1;

      if (
        !hasSerializedCanvasState(campaignData.templateHtml) &&
        isLegacyCircleCandidate &&
        container.clientWidth > 0 &&
        container.clientHeight > 0
      ) {
        const correctedHeightPercent = (widthPercent * container.clientWidth) / container.clientHeight;
        shape.style.height = `${correctedHeightPercent}%`;
      }
      
      // Get shape dimensions
      const shapeRect = shape.getBoundingClientRect();
      const shapeWidth = shapeRect.width;
      const shapeHeight = shapeRect.height;
      
      // Ensure text spans don't overflow and auto-adjust font size
      const spans = shape.querySelectorAll('span');
      spans.forEach((span) => {
        span.style.overflow = 'hidden';
        span.style.textOverflow = 'ellipsis';
        span.style.maxWidth = '100%';
        span.style.maxHeight = '100%';
        span.style.display = 'block';
        span.style.wordWrap = 'break-word';
        span.style.wordBreak = 'break-word';
        span.style.textAlign = 'center';
        
        // Auto-adjust font size if text overflows
        if (shapeWidth > 0 && shapeHeight > 0) {
          let fontSize = parseFloat(span.style.fontSize) || parseFloat(window.getComputedStyle(span).fontSize) || 24;
          const originalFontSize = fontSize;
          const minFontSize = 8;
          const padding = 10; // Account for padding/margins
          const availableWidth = shapeWidth - padding;
          const availableHeight = shapeHeight - padding;
          
          // Create a temporary element to measure text with word wrap
          const tempSpan = span.cloneNode(true);
          tempSpan.style.visibility = 'hidden';
          tempSpan.style.position = 'absolute';
          tempSpan.style.top = '-9999px';
          tempSpan.style.left = '-9999px';
          tempSpan.style.width = `${availableWidth}px`;
          tempSpan.style.whiteSpace = 'normal';
          tempSpan.style.wordWrap = 'break-word';
          tempSpan.style.wordBreak = 'break-word';
          document.body.appendChild(tempSpan);
          
          // Reduce font size until text fits
          while (fontSize > minFontSize) {
            tempSpan.style.fontSize = `${fontSize}px`;
            const textHeight = tempSpan.scrollHeight;
            
            // Check if text fits within available height
            if (textHeight <= availableHeight) {
              break;
            }
            
            fontSize = Math.max(minFontSize, fontSize * 0.9); // Reduce by 10%
          }
          
          document.body.removeChild(tempSpan);
          
          // Apply the adjusted font size if it was reduced
          if (fontSize < originalFontSize) {
            span.style.fontSize = `${fontSize}px`;
          }
        }
      });
    });
  }, [campaignData.templateHtml, renderWidth, renderHeight]);

  return (
    <>
      {/* ✅ Centered Image */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "24px 0",
        }}
      >
        <div
          style={{
            position: "relative",
            backgroundImage: `url("${encodeURI(img)}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: renderWidth ? `${renderWidth}px` : "100%",
            height: renderHeight ? `${renderHeight}px` : "400px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            overflow: "hidden",
          }}
        >
          <div
            ref={templateBodyRef}
            className="content_div"
            dangerouslySetInnerHTML={{
              __html: campaignData.templateHtml,
            }}
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              padding: 0,
              margin: 0,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          />
        </div>
      </div>

      <p style={{ textAlign: "center", margin: "16px 0", fontSize: "16px" }}>
        This is the last step to edit before you can post to social media. Want
        to make edits? Click <b>Go back</b> and make changes.
      </p>

      <div
        className="social-media-header"
        style={{ textAlign: "center", fontWeight: 600 }}
      >
        Social Media
      </div>

      <ModalContent>
        <PluginContainer>
          <Header>
            <Title>Social Media Campaign Creator</Title>
            <Subtitle>
              Create and post content to Facebook and Instagram
            </Subtitle>
          </Header>
          <Content>
            <SelectSocialPlatfrom />
          </Content>
        </PluginContainer>
      </ModalContent>
    </>
  );
};

export default EventSocialPost;

